<template>
  <section class="main">
    <loader v-if="!loaded" />
    <aside v-if="loaded" class="sidebar">
      <div class="sidebar__my">
        <div class="header">
          <template v-if="isDeviceLinked">
            <span class="header__active">{{ $t("ACTIVE_DEVICE") }}</span>
            <img class="header__img" :src="currentDevice.icon" />
            <div class="header__desc">
              <span class="header__title">{{ currentDevice.model }} <span v-if="currentDevice.name">{{
                currentDevice.name }}</span></span>
              <span class="header__device">{{ currentDevice.ios }}</span>
              <ul class="header__list">
                <template v-if="currentDevice.plus">
                  <li class="header__divider"></li>
                  <li class="header__status header__status_pro">Plus</li>
                </template>
                <template v-if="currentDevice.premium">
                  <li class="header__divider"></li>
                  <li class="header__status header__status_premium">PREMIUM</li>
                </template>
              </ul>
            </div>
          </template>

          <template v-else>
            <img class="header__img" src="@/assets/icon-nodevice.svg" />
            <div class="header__desc">
              <span class="header__error-status g-status g-status_error">{{
                $t("NO_DEVICE_LINKED")
                }}</span>
              <router-link to="/link" class="g-button">{{
                $t("LINK_YOUR_DEVICE")
                }}</router-link>
            </div>
          </template>
        </div>
        <nav>
          <ul class="nav" :class="{ nav_disabled: !isDeviceLinked }">
            <li class="nav__item" v-for="item in menuItems" :key="item.title">
              <a v-if="item.external" class="nav__link" :href="item.link" :target="item.target">
                {{ item.title }}
              </a>
              <router-link v-else class="nav__link" :to="item.link">{{
                item.title
                }}</router-link>
            </li>
          </ul>
        </nav>
      </div>
    </aside>

    <div v-if="loaded" class="content">
      <h1 class="intro">{{ $t("ENABLE_JIT_TITLE") }}</h1>
      <div class="row">
        <div class="content-inner">
          <p class="g-text g-text_justify">{{ $t("ENABLE_JIT_INTRO") }}</p>

          <template v-if="isDeviceLinked">
            <ol class="steps">
              <li class="steps__step">
                <html-with-links
                  :html="$t('ENABLE_JIT_STEP_1', { href_jitpair: '/appdb-jitpair', href_configure: '/my/configure' })" />
              </li>
              <li class="steps__step">
                <html-with-links
                  :html="$t('ENABLE_JIT_STEP_2', { href: 'https://apps.apple.com/us/app/localdevvpn/id6755608044' })" />
              </li>
              <li class="steps__step">
                <html-with-links
                  :html="$t('ENABLE_JIT_STEP_3', { href: '/details/45a698af5360560fd8a522a8ebbc634da8f55df4' })" />
              </li>
            </ol>
          </template>

          <template v-else>
            <div class="no-device-container">
              <img class="no-device-img" src="@/assets/icon-nodevice.svg" />
              <div class="no-device-desc">
                <span class="no-device-error g-status g-status_error">{{ $t("NO_DEVICE_LINKED") }}</span>
                <router-link to="/link" class="g-button">{{ $t("LINK_YOUR_DEVICE") }}</router-link>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- JIT Capability Check -->
      <div v-if="isDeviceLinked" class="jit-check-container">
        <important-message v-if="currentDevice.can_use_jit === 'yes'" type="info">
          <div v-html="$t('CURRENT_DEVICE_CAN_USE_JIT_NOTICE', { href: '/my/installation-history' })"></div>
        </important-message>

        <important-message v-else-if="currentDevice.can_use_jit === 'no'" type="notice">
          <div v-html="currentDevice.can_use_jit_validation_error.translated"></div>
        </important-message>

        <div class="button-container" v-if="currentDevice.can_use_jit !== 'yes'">
          <button class="g-button jit-check-button" @click="checkJITCapability">
            {{ $t("CHECK_AGAIN") }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import Loader from '@/components/Loader';
import getMainMenuItems from '@/utils/MainMenu';
import ImportantMessage from '@/components/ImportantMessage.vue';

export default {
  components: {
    HtmlWithLinks,
    Loader,
    ImportantMessage,
  },
  data() {
    return {
      menuItems: [],
    };
  },
  computed: {
    title() {
      return this.$t('ENABLE_JIT_TITLE');
    },
    loaded() {
      const { configLoaded, devicesLoaded } = this.$store.getters;
      return configLoaded && devicesLoaded;
    },
    isDeviceLinked() {
      return this.$store.getters.isDeviceLinked;
    },
    currentDevice() {
      return this.$store.getters.devices
        .find(device => device.lt === this.$store.getters.lt) || {};
    },
  },
  methods: {
    checkJITCapability() {
      Promise.all([
        this.$store.dispatch('getDevices'),
        this.$store.dispatch('getConfig'),
      ]);
    },
  },
  created() {
    this.menuItems = getMainMenuItems();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: flex;
}

.main::v-deep .intro {
  background: var(--my-heading-bg);
  border-radius: 2px;
  font-size: 16px;
  font-weight: 600;
  padding: 10px 15px;
  display: block;
  margin-bottom: 25px;

  @include notebook {
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    padding: 7px 10px;
    margin-bottom: 15px;
  }
}

.sidebar {
  flex: 0 0 400px;
  margin-right: 30px;

  @include notebook {
    display: none;
  }

  &__my {
    background: var(--bg);
    border: 1px solid var(--tertiary-accent);
    box-shadow: 0 4px 10px 0 var(--shadow-color-1);
    border-radius: 8px;
    overflow: hidden;
  }
}

.content {
  flex: 1;
}

.content-inner {
  flex-grow: 1;

  @include notebook {
    order: 1;
  }
}

.header {
  padding: 20px 25px;
  display: flex;
  align-items: center;
  position: relative;

  &__error-status {
    font-size: 14px;
    margin-bottom: 10px;
  }

  &__title {
    color: var(--text-primary);
    font-weight: 500;
    display: block;
    margin-bottom: 10px;
  }

  &__active {
    background: var(--tertiary-accent);
    border-radius: 2px;
    padding: 4px;
    color: var(--text-tertiary-accent);
    font-size: 10px;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__img {
    height: 90px;
    margin-right: 15px;
    filter: var(--img-filter);
  }

  &__list {
    display: flex;
    margin-top: 8px;
  }

  &__status {
    border-radius: 2.5px;
    display: flex;
    align-items: center;
    padding: 3px;

    &_jail {
      font-size: 12px;
      background: var(--tertiary-accent);
      color: var(--text-tertiary-accent);

      &-active {
        color: #053305;
        background: var(--bg-success);
      }
    }

    &_pro {
      text-transform: uppercase;
      background: $primary;
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      padding-bottom: 2px;
    }

    &_premium {
      background: $primary;
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      padding-bottom: 2px;
    }
  }

  &__divider {
    background: #d8d8d8;
    width: 5px;
    height: 5px;
    border-radius: 100%;
    margin: auto 5px;
  }
}

.nav__link,
.links__link {
  display: block;
  padding: 10px 15px;
  color: var(--text-primary);
}

.nav,
.links {
  &_disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.nav {
  &__link {
    border-top: 1px solid var(--tertiary-accent);

    &.router-link-active {
      background-image: var(--pink-gradient);
    }
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-wrap: wrap;
  }
}

.g-text {
  margin-bottom: 10px;
  line-height: 1.2;
}

.steps {
  counter-reset: steps;
  margin-top: 20px;

  &__step {
    display: flex;
    align-items: center;
    font-size: 16px;

    &:not(:last-child) {
      margin-bottom: 15px;

      @include tablet {
        margin-bottom: 10px;
      }
    }

    &::before {
      counter-increment: steps;
      content: counter(steps) ".";
      font-size: 25px;
      margin-right: 10px;
      line-height: 1;

      @include tablet {
        font-size: 18px;
      }
    }
  }

  &__icon {
    margin: 0 10px 0 0;
    max-width: 50px;
    width: 100%;
    align-self: center;

    @include tablet {
      max-width: 40px;
    }
  }
}

.no-device-container {
  display: flex;
  align-items: center;
  margin-top: 30px;
}

.no-device-img {
  height: 90px;
  margin-right: 15px;
  filter: var(--img-filter);
}

.no-device-desc {
  display: flex;
  flex-direction: column;
}

.no-device-error {
  font-size: 14px;
  margin-bottom: 10px;
}

.jit-check-container {
  margin-top: 30px;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 15px;
}
</style>
